import { Location, NgIf, NgTemplateOutlet, NgFor, CurrencyPipe } from '@angular/common';
import { AfterViewInit, Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { faAngleLeft, faFileDownload, faFilter, faRotateLeft, faSortDown, faSortUp, faSortAmountDownAlt } from '@fortawesome/free-solid-svg-icons';
import { NgbModal, NgbTooltip, NgbPopover, NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, NgbDropdownButtonItem, NgbDropdownItem, NgbPagination, NgbCollapse } from '@ng-bootstrap/ng-bootstrap';
import { Chart } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import dayjs from 'dayjs';
import { environment } from 'src/environments/environment';
import { AnalyticsService } from '../analytics.service';
import { DatePickerControllerComponent } from '../date-picker-controller/date-picker-controller.component';
import { Payment } from '../payment';
import { PaymentCategoryPipe } from '../payment-category.pipe';
import { PaymentModalComponent } from '../payment-modal/payment-modal.component';
import { PaymentStatusPipe } from '../payment-status.pipe';
import { PaymentService } from '../payment.service';
import { TableControlService } from '../table-control.service';
import { Subject, Subscription, debounceTime } from 'rxjs';
import { LoaderService } from '../shared/services/loader.service';
import { FormatDateTimePipe } from '../format-date-time.pipe';
import { NgxSpinnerComponent } from 'ngx-spinner';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { DarkModeService } from '@app-shared/services/dark-mode.service';

@Component({
  selector: 'app-reports-payments',
  templateUrl: './reports-payments.component.html',
  styleUrls: ['./reports-payments.component.css'],
  providers: [PaymentCategoryPipe, PaymentStatusPipe],
  imports: [NgbTooltip, FaIconComponent, DatePickerControllerComponent, NgIf, NgTemplateOutlet, NgbPopover, NgFor, NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, NgbDropdownButtonItem, NgbDropdownItem, NgbPagination, NgbCollapse, NgxSpinnerComponent, CurrencyPipe, FormatDateTimePipe, PaymentStatusPipe, PaymentCategoryPipe, FormsModule]
})
export class ReportsPaymentsComponent implements OnInit, AfterViewInit {

  // Screensize Event Listener
  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.resizeSubject.next((event.target as Window).innerWidth);
  }

  // Variable Subject
  private resizeSubject: Subject<number> = new Subject<number>();

  // Properties
  @ViewChild('datePickerController') private datePickerController: DatePickerControllerComponent;
  payments: Payment[] = [];
  paymentCount: number = 0;
  startDate: string;
  endDate: string;
  isMobileView: boolean = false;
  storedSearchValue: string;

  // Category Volume Chart
  categoryChartData: any[];
  categoryTotal: number = 0;
  filteredCategoryTotal: number = 0;
  categoryChart: Chart;
  categoryFilter: string = 'All'; // 'All' or any category key
  categoryFilterOptions: Array<{ value: string, label: string }> = [];

  // Monthly Volume Chart
  monthlyVolumeChartData: {
    actualPayments: any[],
    creditPayments: any[]
  } = { actualPayments: [], creditPayments: [] };
  monthlyVolumeTotal: number = 0;
  monthlyVolumeChartLabels: string[] = [];
  monthlyVolumeChart: Chart;

  gridColor: string = '#dddddd';
  legendColor: string = '#000000';
  dataLabelColor: string = '#000000';
  isDarkMode: boolean;
  private darkModeSubscription: Subscription;

  private CATEGORIES: any = {
    'CARD': { position: 1, backgroundColor: 'rgba(255, 99, 132, 0.5)' },
    'ACH': { position: 2, backgroundColor: 'rgba(255, 159, 64, 0.5)' },
    'CHECK': { position: 3, backgroundColor: 'rgba(0, 0, 255, 0.5)' },
    'CASH': { position: 4, backgroundColor: 'rgba(16, 94, 34, 0.5)' },
    'PAYPAL': { position: 5, backgroundColor: 'rgba(255, 204, 0, 0.5)' }, // Official Color
    'VENMO': { position: 6, backgroundColor: 'rgba(0, 140, 255, 0.5)' }, // Official Color
    'CASH APP': { position: 7, backgroundColor: 'rgba(4, 140, 44, 0.5)' }, // Official Color
    'ZELLE': { position: 8, backgroundColor: 'rgba(103, 28, 202, 0.5)' }, // Official Color,
    'SHORT_PAYMENT': { position: 9, backgroundColor: 'rgba(139, 0, 0, 0.5)' },
    'CREDIT NOTE': { position: 10, backgroundColor: 'rgba(34, 139, 34, 0.5)' },
    'OTHER': { position: 11, backgroundColor: 'rgba(128, 128, 128, 0.5)' }
  };

  sortOptions = [
    { label: 'Client', value: 'c.client_name' },
    { label: 'Invoice #', value: 'i.invoice_number' },
    { label: 'Date', value: 'pay.payment_date, pay.payment_created_at' },
    { label: 'Category', value: 'pay.payment_category' },
    { label: 'Status', value: 'pay.payment_status' },
  ];
  filterColumns = [
    { label: 'Category', value: 'pay.payment_category' },
    { label: 'Status', value: 'pay.payment_status' },
  ];
  sortBy: string = 'c.client_name';
  sortDirection: string = 'ASC';

  // Table Controls
  tableControls: TableControlService;

  // Filters
  columnFilterValues = new Map();
  activeFilters = new Map();
  activeFilterCount: number = 0;
  selectedFilterColumn: string = '';

  // Font Awesome Properties
  faAngleLeft = faAngleLeft;
  faRotateLeft = faRotateLeft;
  faSortUp = faSortUp;
  faSortDown = faSortDown;
  faFilter = faFilter;
  faFileDownload = faFileDownload;
  faSortAmountDownAlt = faSortAmountDownAlt;

  // Get isMobile value in reactive way
  public get isMobile(): boolean {
    return this.isMobileView;
  }

  constructor(private paymentService: PaymentService,
    private analyticsService: AnalyticsService,
    private modalService: NgbModal,
    private paymentCategoryPipe: PaymentCategoryPipe,
    private paymentStatusPipe: PaymentStatusPipe,
    public location: Location,
    private loaderService: LoaderService,
    private darkModeService: DarkModeService) {
    this.resizeSubject.pipe(
      debounceTime(100)
    ).subscribe(width => {
      if (width <= 768) {
        this.checkScreenSize(width);
      } else {
        this.isMobileView = false;
        this.tableControls.setLimit(20);
      }
    });
  }

  ngOnInit(): void {
    // Initialize category filter options
    this.categoryFilterOptions = [
      { value: 'All', label: 'All' }
    ];
    // Add all categories to filter options
    Object.keys(this.CATEGORIES).forEach(category => {
      this.categoryFilterOptions.push({
        value: category,
        label: this.paymentCategoryPipe.transform(category)
      });
    });
    
    this.tableControls = new TableControlService('Reports.Payments', true, 'pay.payment_date, pay.payment_created_at', 'DESC');
    this.darkModeSubscription = this.darkModeService.darkModeChanged$.subscribe((isDarkModeEnabled) => {
      if (isDarkModeEnabled) {
        this.gridColor = '#444444';
        this.legendColor = '#ffffff';
        this.dataLabelColor = '#ffffff';
      }
      this.updateChart(isDarkModeEnabled);
    });
    this.checkScreenSize();
    if (this.isMobile || this.isMobileView) {
      let activeFilters = this.tableControls.getActiveFilters()
      let columns = [];
      for (const [key, value] of activeFilters) {
        columns.push(key)
      }
      this.tableControls.resetFilters(columns)
    }
    this.tableControls.refresh.subscribe(() => {
      this.getPayments();
      this.getCategoryChartData();
      this.getMonthlyVolumeChartData();
    });
     if (this.tableControls.hasTableConfiguration()) this.storedSearchValue = this.tableControls.getSearchTerm();
  }

  ngAfterViewInit(): void {
    this.tableControls.datePickerController = this.datePickerController;
  }

  ngOnDestroy(): void {
    if (this.darkModeSubscription) {
      this.darkModeSubscription.unsubscribe();
    }
    this.modalService.hasOpenModals() && this.modalService.dismissAll();
  }

  // Open Payment Modal
  openPaymentModal(payment: Payment): void {
    const modalRef = this.modalService.open(PaymentModalComponent);
    modalRef.componentInstance.payment = payment;
    modalRef.componentInstance.showInvoiceLink = true;
    modalRef.componentInstance.saved.subscribe(() => {
      this.getPayments();
    });
  }

  // Date Changed
  dateChanged(event: { startDate: string, endDate: string, isAuto: boolean }): void {
    this.loaderService.showSpinner();
    this.startDate = event.startDate;
    this.endDate = event.endDate;
    this.tableControls.addRangeFilter('pay.payment_date', this.startDate, this.endDate, event.isAuto);
  }

  // Get Payments
  private getPayments(): void {
    let params = this.tableControls.getParams();
    if (this.isMobile) {
      params.sortBy = this.sortBy;
      params.sortDirection = this.sortDirection;
      for (const [key, value] of this.activeFilters) {
        if (key.includes('range')) params[`filter:${key}`] = value;
        else params[`filter:${key}`] = JSON.stringify(value);
      }
    };
    this.paymentService.getPayments(params).subscribe({
      next: (res) => {
        this.payments = res.payments;
        this.paymentCount = res.count;
        this.loaderService.hideSpinner(700);
      },
      error: () => {
        this.loaderService.hideSpinner(700);
      }
    });
  }

  // Get Category Chart Data
  private getCategoryChartData(): void {
    const params: any = {
      startDate: this.startDate,
      endDate: this.endDate
    };
    this.analyticsService.getPaymentCategoryChartData(params).subscribe((chartData) => {
      this.categoryChartData = (chartData as any[]).sort((d1, d2) => { return this.CATEGORIES[d1.category]?.position - this.CATEGORIES[d2.category]?.position; }).map((data: any) => {
        return {
          label: this.paymentCategoryPipe.transform(data.category),
          value: data.total,
          backgroundColor: this.CATEGORIES[data.category]?.backgroundColor,
          category: data.category // Store original category for filtering
        };
      });
      this.categoryTotal = (chartData as any[]).reduce((acc: number, data: any) => { return acc + data.total; }, 0);
      if (!this.categoryChart) {
        this.generateCategoryChart();
        // Apply filter after chart is generated
        setTimeout(() => {
          this.onCategoryFilterChange();
        }, 0);
      } else {
        if (this.categoryChartData.length === 0) {
          this.categoryChart.data.labels.length = 0;
          this.categoryChart.data.datasets[0].data.length = 0;
        } else {
          this.categoryChart.data.labels = this.categoryChartData.map((chartData) => { return chartData.label; });
          this.categoryChart.data.datasets[0].data = this.categoryChartData.map((chartData) => { return chartData.value; });
          this.categoryChart.data.datasets[0].backgroundColor = this.categoryChartData.map((chartData) => { return chartData.backgroundColor; });
        }
        this.categoryChart.update();
        // Apply current filter after data update
        setTimeout(() => {
          this.onCategoryFilterChange();
        }, 0);
      }
    });
  }

  // Get Monthly Volume Chart Data
  private getMonthlyVolumeChartData(): void {
    const params: any = {
      startDate: this.startDate,
      endDate: this.endDate
    };
    this.analyticsService.getPaymentMonthlyVolumeChartData(params).subscribe((chartData) => {
      this.monthlyVolumeChartData.actualPayments = chartData?.actualPayments.map((data: any) => {
        return { label: dayjs().month(data.month - 1).format('MMMM'), value: data.total };
      });
      this.monthlyVolumeChartData.creditPayments = chartData?.creditPayments.map((data: any) => {
        return { label: dayjs().month(data.month - 1).format('MMMM'), value: data.total };
      });
      this.monthlyVolumeTotal = chartData?.total;

      const allMonthlyPaymentData: any[] = chartData?.actualPayments;
      for (const creditPayment of chartData?.creditPayments) {
        const index = allMonthlyPaymentData.findIndex((payment) => { return payment.month == creditPayment.month; });
        if (index >= 0) {
          allMonthlyPaymentData[index].total += creditPayment.total;
        } else {
          allMonthlyPaymentData.push({ month: creditPayment.month, total: creditPayment.total });
        }
      }
      this.monthlyVolumeChartLabels = allMonthlyPaymentData.sort((p1, p2) => {
        return p1.month - p2.month;
      }).map((payment: any) => {
        const total: string = ' (' + new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(payment.total) + ')';
        return dayjs().month(payment.month - 1).format('MMMM') + total;
      });

      if (!this.monthlyVolumeChart) {
        this.generateMonthlyVolumeChart();
      } else {
        if (this.monthlyVolumeChartData.actualPayments.length === 0 && this.monthlyVolumeChartData.creditPayments.length === 0) {
          this.monthlyVolumeChart.data.labels.length = 0;
          this.monthlyVolumeChart.data.datasets[0].data.length = 0;
          this.monthlyVolumeChart.data.datasets[1].data.length = 0;
        } else {
          this.monthlyVolumeChart.data.labels = this.monthlyVolumeChartLabels;
          // Actual Payments
          this.monthlyVolumeChart.data.datasets[0].data = this.monthlyVolumeChartData.actualPayments.map((chartData) => { return chartData.value; });
          // Credit Payments
          this.monthlyVolumeChart.data.datasets[1].data = this.monthlyVolumeChartData.creditPayments.map((chartData) => { return chartData.value; });
        }
        this.monthlyVolumeChart.update();
      }
    });
  }

  /* ----- Chart ----- */

  // Update Chart
  private updateChart(isDarkMode: boolean): void {
    if (this.categoryChart) {
      this.categoryChart.options.plugins.legend.labels.color = isDarkMode ? '#ffffff' : '#000000';
      this.categoryChart.options.plugins.datalabels.color = isDarkMode ? '#ffffff' : '#000000';
      this.categoryChart.update();
    }
    if (this.monthlyVolumeChart) {
      this.monthlyVolumeChart.options.plugins.legend.labels.color = isDarkMode ? '#ffffff' : '#000000';
      this.monthlyVolumeChart.options.plugins.datalabels.color = isDarkMode ? '#ffffff' : '#000000';
      this.monthlyVolumeChart.options.plugins.datalabels.color = isDarkMode ? '#ffffff' : '#000000';
      this.monthlyVolumeChart.options.scales.x.grid.color = isDarkMode ? '#ffffff' : '#000000';
      this.monthlyVolumeChart.options.scales.y.grid.color = isDarkMode ? '#ffffff' : '#000000';
      this.monthlyVolumeChart.update();
    }
  }

  // Category Filter Change Handler
  onCategoryFilterChange(): void {
    if (!this.categoryChart || !this.categoryChartData) {
      return;
    }

    const meta = this.categoryChart.getDatasetMeta(0);

    for (let i = 0; i < meta.data.length && i < this.categoryChartData.length; i++) {
      const dataPoint = meta.data[i] as any;
      const originalCategory = this.categoryChartData[i].category;

      if (this.categoryFilter === 'All') {
        // Show all categories
        dataPoint.hidden = false;
      } else {
        // Show only the selected category, hide all others
        dataPoint.hidden = originalCategory !== this.categoryFilter;
      }
    }

    // Update the chart
    this.categoryChart.update();

    // Recalculate filtered total
    setTimeout(() => {
      this.calculateFilteredCategoryTotal();
    }, 10);
  }

  // Calculate Filtered Category Total
  private calculateFilteredCategoryTotal(): void {
    if (!this.categoryChart || !this.categoryChartData) {
      this.filteredCategoryTotal = this.categoryTotal;
      return;
    }

    const meta = this.categoryChart.getDatasetMeta(0);
    let total = 0;

    for (let i = 0; i < meta.data.length && i < this.categoryChartData.length; i++) {
      const dataPoint = meta.data[i] as any;
      // Check if the data point is visible (not hidden)
      // hidden is false/undefined when visible, true when hidden
      if (!dataPoint.hidden) {
        total += this.categoryChartData[i].value;
      }
    }

    this.filteredCategoryTotal = total;
  }

  // Generate Category Chart
  private generateCategoryChart(): void {
    const ctx = (document.getElementById('CATEGORY_CHART') as HTMLCanvasElement);
    this.categoryChart = new Chart(ctx, {
      type: 'pie',
      data: {
        labels: this.categoryChartData.map((chartData) => { return chartData.label; }),
        datasets: [
          {
            label: null,
            data: this.categoryChartData.map((chartData) => { return chartData.value; }),
            backgroundColor: this.categoryChartData.map((chartData) => { return chartData.backgroundColor; })
          }
        ]
      },
      plugins: [
        ChartDataLabels
      ],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false
        },
        plugins: {
          legend: {
            display: true,
            labels: {
              color: this.legendColor,
              font: {
                family: "'Poppins', sans-serif"
              }
            },
            position: 'bottom',
            onHover: (event, legendItem) => {
              console.log(event, legendItem);
              const target = event.native.target as HTMLElement;
              if(target) {
                target.style.cursor = 'pointer';
              }
            }
          },
          tooltip: {
            enabled: true,
            backgroundColor: 'rgba(0, 0, 0, 1)',
            xAlign: 'center',
            yAlign: 'bottom',
            boxPadding: 6,
            callbacks: {
              label: (context) => {
                let label = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(context.parsed);
                const filteredTotal = this.filteredCategoryTotal || this.categoryTotal;
                label += ' (' + ((context.parsed / filteredTotal) * 100).toFixed(2) + '%)';
                return label;
              }
            }
          },
          datalabels: {
            color: this.dataLabelColor,
            font: {
              family: "'Poppins', sans-serif",
              weight: 600
            },
            display: 'auto',
            formatter: (value, context) => {
              if (value > 0) {
                const filteredTotal = this.filteredCategoryTotal || this.categoryTotal;
                let label = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
                label += '\n(' + ((value / filteredTotal) * 100).toFixed(2) + '%)';
                return label;
              }
              return null;
            },
            textAlign: 'center'
          }
        },
        animation: {
          onComplete: () => {
            // Recalculate filtered total after animation completes
            this.calculateFilteredCategoryTotal();
          }
        }
      }
    });

    // Initialize filtered total
    this.calculateFilteredCategoryTotal();
  }

  // Generate Monthly Volume Chart
  private generateMonthlyVolumeChart(): void {
    const ctx = (document.getElementById('MONTHLY_VOLUME_CHART') as HTMLCanvasElement);
    this.monthlyVolumeChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: this.monthlyVolumeChartLabels,
        datasets: [
          {
            label: 'Actual Payments',
            data: this.monthlyVolumeChartData.actualPayments.map((chartData) => { return chartData.value; }),
            backgroundColor: 'rgba(16, 94, 34, 0.5)'
          },
          {
            label: 'Credit Payments',
            data: this.monthlyVolumeChartData.creditPayments.map((chartData) => { return chartData.value; }),
            backgroundColor: 'rgba(255, 99, 132, 0.5)'
          }
        ]
      },
      plugins: [ChartDataLabels],
      options: {
        events: null,
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 15
          }
        },
        scales: {
          x: {
            display: true,
            stacked: true,
            grid: {
              color: this.gridColor
            }
          },
          y: {
            stacked: true,
            ticks: {
              callback: (value) => {
                return '$' + value;
              }
            }
          }
        },
        plugins: {
          legend: {
            display: true,
            labels: {
              color: 'black',
              font: {
                family: "'Poppins', sans-serif"
              }
            },
            position: 'bottom'
          },
          tooltip: {
            enabled: false
          },
          datalabels: {
            align: 'top',
            anchor: 'end',
            color: this.dataLabelColor,
            font: {
              family: "'Poppins', sans-serif",
              weight: 600
            },
            formatter: (value) => {
              return (value > 0) ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value) : null;
            },
            offset: (context) => {
              // Create gap between Actual and Credit Payments labels
              const datasetIndex = context.datasetIndex;
              const chart = context.chart;
              const creditValue = chart.data.datasets[1].data[context.dataIndex] as number || 0;

              // If this is actual payments and there's credit payment above, position lower to create gap
              if (datasetIndex === 0 && creditValue > 0) {
                return -20; // Negative offset creates gap below credit payment label
              }
              // Default offset
              return -2.5;
            },
            textAlign: 'center'
          }
        }
      }
    });
  }

  // Download Report
  downloadReport(): void {
    const searchParams = this.tableControls.getUrlSearchParams();
    searchParams.delete('limit');
    searchParams.delete('offset');
    const url = new URL('/api/reports/finances/payments/download?' + searchParams.toString(), environment.apiUrl);
    window.open(url.toString());
  }

  /* ----- Filters and Sorting ----- */

  // Get Distinct Column Values
  getDistinctColumnValues(column: string): void {
    this.selectedFilterColumn = column;
    if (!this.columnFilterValues.has(column)) {
      const params = {
        searchTerm: null,
        sortBy: column,
        sortDirection: 'ASC',
        limit: null,
        offset: null,
        column: column
      };
      if (this.isMobile) {
        for (const [key, value] of this.activeFilters) {
          if (key.includes('range')) params[`filter:${key}`] = value;
          else params[`filter:${key}`] = JSON.stringify(value);
        }
      }
      this.paymentService.getDistinctColumnValues(params).subscribe((values) => {
        const tempArray = [];
        for (const value of values) {
          let name = null;
          switch (column) {
            case 'pay.payment_category':
              name = this.paymentCategoryPipe.transform(value);
              break;
            case 'pay.payment_status':
              name = this.paymentStatusPipe.transform(value);
              break;
            default:
              name = (value === null || value.length == 0) ? 'BLANK' : value;
              break;
          }
          tempArray.push({ name: name, value: value, randomId: Math.random().toString(36) });
        }
        this.columnFilterValues.set(column, tempArray);
      });
    }
  }

  // Sorting
  sorting(column: string): void {
    if (column != this.sortBy) this.sortDirection = 'ASC';
    if (column == this.sortBy) this.sortDirection = (this.sortDirection == 'ASC') ? 'DESC' : 'ASC';
    this.sortBy = column;
    this.getPayments();
  }

  // Toggle Filter
  toggleFilter(column: string, value: string): void {
    if (this.activeFilters.has(column)) {
      if (this.activeFilters.get(column).includes(value)) {
        let tempArray = this.activeFilters.get(column);
        tempArray = tempArray.filter((filterValue) => { return filterValue != value; });
        if (tempArray.length == 0) {
          this.activeFilters.delete(column);
        } else {
          this.activeFilters.set(column, tempArray);
        }
      } else {
        const tempArray = this.activeFilters.get(column);
        tempArray.push(value);
        this.activeFilters.set(column, tempArray);
      }
    } else {
      this.activeFilters.set(column, [value]);
    }
    this.getPayments();
    this.getActiveFilterCount();
  }

  // Get Active Filter Count
  getActiveFilterCount(): void {
    this.activeFilterCount = 0;
    for (const value of this.activeFilters.values()) {
      this.activeFilterCount += value.length;
    }
  }

  // Filter Exists
  filterExists(column: string, value: string): boolean {
    return this.activeFilters.has(column) ? this.activeFilters.get(column).includes(value) : false;
  }

  // Reset Active Filters
  resetActiveFilters(): void {
    this.activeFilters.clear();
    this.getPayments();
    this.getActiveFilterCount();
  }

  // Show Column Filter Popover
  showColumnFilterPopover(popover, column: string): void {
    this.getDistinctColumnValues(column);
    if (popover.isOpen()) {
      popover.close();
    } else {
      setTimeout(() => {
        popover.open({ column });
      }, 250);
    }
  }

  //Set isMobile value and set table limit base on screen size
  checkScreenSize(width?: number) {
    let screenWidth = width || window.innerWidth;
    this.isMobileView = screenWidth <= 768;
    this.isMobileView ? this.tableControls.setLimit(5) : this.tableControls.setLimit(20);
  }
}
